import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  searchPageHref,
  searchPages,
  visibleSearchPages,
  type SearchPage,
} from '@resort-os/config';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';

function headerSearchInput(target: EventTarget | null): HTMLInputElement | null {
  if (!(target instanceof HTMLInputElement)) return null;
  if (!target.closest('header')) return null;
  const className = typeof target.className === 'string' ? target.className : '';
  const placeholder = target.getAttribute('placeholder') || '';
  if (className.includes('searchInput') || placeholder.toLowerCase().startsWith('search')) {
    return target;
  }
  return null;
}

function clearHeaderQuery(input: HTMLInputElement | null) {
  if (!input) return;
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(input, '');
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

function openPage(page: SearchPage) {
  const href = searchPageHref(page);
  const url = new URL(href);
  if (url.origin !== window.location.origin) {
    window.location.assign(href);
    return;
  }
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next === current) return;
  window.history.pushState(null, '', next);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function HeaderPageSearch() {
  const { user } = useAuth();
  const { apps, hasPermission } = usePermissions();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef({ open, active, results: [] as SearchPage[] });

  const pages = useMemo(
    () =>
      visibleSearchPages({
        apps,
        isAdmin: (user?.role || '').toLowerCase() === 'admin',
        canViewSettings: hasPermission('settings.view'),
      }),
    [apps, hasPermission, user?.role],
  );
  const results = useMemo(() => searchPages(query, pages), [pages, query]);
  stateRef.current = { open, active, results };

  useEffect(() => {
    const place = (input: HTMLInputElement) => {
      inputRef.current = input;
      setAnchor(input.parentElement?.getBoundingClientRect() ?? input.getBoundingClientRect());
    };

    const onFocusIn = (event: FocusEvent) => {
      const input = headerSearchInput(event.target);
      if (!input) return;
      place(input);
      setQuery(input.value);
      setOpen(true);
      setActive(0);
    };

    const onInput = (event: Event) => {
      const input = headerSearchInput(event.target);
      if (!input) return;
      place(input);
      setQuery(input.value);
      setOpen(true);
      setActive(0);
    };

    const choose = (page: SearchPage) => {
      setOpen(false);
      clearHeaderQuery(inputRef.current);
      openPage(page);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const input = headerSearchInput(event.target);
      const current = stateRef.current;
      if (!input || !current.open) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(index => Math.min(index + 1, Math.max(current.results.length - 1, 0)));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(index => Math.max(index - 1, 0));
      } else if (event.key === 'Escape') {
        setOpen(false);
      } else if (event.key === 'Enter' && current.results[current.active]) {
        event.preventDefault();
        choose(current.results[current.active]);
      }
    };

    const onFocusOut = () => {
      window.setTimeout(() => {
        const current = document.activeElement;
        if (popupRef.current?.contains(current)) return;
        if (headerSearchInput(current)) return;
        setOpen(false);
      }, 0);
    };

    const onReposition = () => {
      const input = inputRef.current;
      if (!input) return;
      setAnchor(input.parentElement?.getBoundingClientRect() ?? input.getBoundingClientRect());
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('input', onInput);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('input', onInput);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, []);

  if (!open || !query.trim() || !anchor) return null;

  const width = 380;
  const left = Math.min(
    Math.max(12, anchor.right - width),
    Math.max(12, window.innerWidth - width - 12),
  );

  return createPortal(
    <div
      ref={popupRef}
      role="listbox"
      aria-label="Pages"
      style={{
        position: 'fixed',
        top: anchor.bottom + 8,
        left,
        width,
        maxHeight: 420,
        overflowY: 'auto',
        background: '#fff',
        border: '1px solid var(--rs-neutral-200, #e4e4e4)',
        zIndex: 1400,
        boxShadow: 'var(--rs-shadow-lg, 0 8px 24px rgba(0,0,0,0.08))',
      }}
    >
      {results.length === 0 ? (
        <div
          style={{ padding: '14px 16px', color: 'var(--rs-neutral-500, #737373)', fontSize: 14 }}
        >
          No matching pages
        </div>
      ) : (
        results.map((page, index) => {
          const selected = index === active;
          return (
            <button
              key={page.id}
              id={`header-page-${page.id.replace(/[^a-z0-9]+/gi, '-')}`}
              type="button"
              role="option"
              aria-selected={selected}
              onMouseEnter={() => setActive(index)}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                setOpen(false);
                clearHeaderQuery(inputRef.current);
                openPage(page);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid var(--rs-neutral-100, #f2f2f2)',
                background: selected ? 'var(--rs-brand-light, #e7f3f0)' : '#fff',
                color: 'var(--rs-neutral-800, #262626)',
                padding: '10px 14px',
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{page.title}</span>
              <span
                style={{
                  display: 'block',
                  marginTop: 2,
                  fontSize: 12,
                  color: 'var(--rs-neutral-500, #737373)',
                }}
              >
                {page.module}
                {page.hint ? ` · ${page.hint}` : ''}
              </span>
            </button>
          );
        })
      )}
    </div>,
    document.body,
  );
}
